import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useTheme } from "@/context/ThemeContext";
import { ShoppingBag, User, Search, Menu, X, LogOut, Shield, Heart, Sun, Moon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const { currency, setCurrency, available } = useCurrency();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");

  const navItem = ({ isActive }) =>
    `text-sm tracking-wide hover:text-[#D4AF37] transition-colors ${
      isActive ? "text-[#D4AF37]" : "text-[#2C1E16] dark:text-[#F5F2ED]"
    }`;

  const onSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/shop?search=${encodeURIComponent(search.trim())}`);
      setSearchOpen(false);
      setSearch("");
    }
  };

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF9F6]/85 dark:bg-[#171717]/95 border-b border-[#EBE5DF] dark:border-[#383838]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Mobile menu */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Logo */}
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
            <img src="/favicon.png" alt="Amazypet" className="h-10 w-10" />
            <span className="hidden sm:block font-serif text-2xl tracking-tight">Amazypet</span>
          </Link>

          {/* Center nav */}
          <nav className="hidden md:flex items-center gap-10">
            <NavLink to="/" end className={navItem} data-testid="nav-home">{t("nav.home")}</NavLink>
            <NavLink to="/shop" className={navItem} data-testid="nav-shop">{t("nav.shop")}</NavLink>
            <NavLink to="/shop?category=skylos" className={navItem}>{t("nav.categories")}</NavLink>
            <NavLink to="/careers" className={navItem} data-testid="nav-careers">{t("nav.careers")}</NavLink>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-4">
            <button
              className="p-2 hover:text-[#D4AF37] transition-colors"
              onClick={() => setSearchOpen((s) => !s)}
              data-testid="search-toggle"
              aria-label="Search"
            >
              <Search size={20} />
            </button>

            {/* Theme toggle */}
            <button
              className="p-2 hover:text-[#D4AF37] transition-colors"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label={theme === "dark" ? "Light mode" : "Dark mode"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
            >
              {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Language switcher */}
            <div className="hidden sm:flex items-center gap-1 text-xs tracking-widest" data-testid="language-switcher">
              <button
                onClick={() => setLang("el")}
                className={`px-2 py-1 transition-colors ${lang === "el" ? "text-[#D4AF37] font-semibold" : "text-[#736B66] hover:text-[#2C1E16]"}`}
                data-testid="lang-el"
              >GR</button>
              <span className="text-[#EBE5DF]">/</span>
              <button
                onClick={() => setLang("en")}
                className={`px-2 py-1 transition-colors ${lang === "en" ? "text-[#D4AF37] font-semibold" : "text-[#736B66] hover:text-[#2C1E16]"}`}
                data-testid="lang-en"
              >EN</button>
            </div>

            {/* Currency switcher */}
            <select
              className="hidden sm:block bg-transparent text-xs tracking-widest text-[#736B66] hover:text-[#2C1E16] border-none focus:outline-none cursor-pointer"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              data-testid="currency-switcher"
              aria-label="Currency"
            >
              {available.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {/* Account */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 hover:text-[#D4AF37] transition-colors" data-testid="account-menu-trigger" aria-label="Account">
                  <User size={20} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-[#EBE5DF] rounded-none">
                {user ? (
                  <>
                    <DropdownMenuItem disabled className="opacity-100 text-[#2C1E16]">
                      <div className="flex flex-col">
                        <span className="font-medium">{user.name}</span>
                        <span className="text-xs text-[#736B66]">{user.email}</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                      <User size={14} className="mr-2" /> {t("nav.account")}
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                        <Shield size={14} className="mr-2" /> {t("nav.admin")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                      <LogOut size={14} className="mr-2" /> {t("nav.logout")}
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/login")} data-testid="menu-login">
                      {t("nav.login")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/register")} data-testid="menu-register">
                      {t("nav.register")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Wishlist */}
            <Link to="/wishlist" className="relative p-2 hover:text-[#8B0000] transition-colors" data-testid="wishlist-link" aria-label="Wishlist">
              <Heart size={20} />
              {wishCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-[#8B0000] text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-semibold"
                  data-testid="wishlist-count"
                >
                  {wishCount}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link to="/cart" className="relative p-2 hover:text-[#D4AF37] transition-colors" data-testid="cart-link" aria-label="Cart">
              <ShoppingBag size={20} />
              {count > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-[#8B0000] text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-semibold"
                  data-testid="cart-count"
                >
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={onSearch} className="pb-4">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("nav.search")}
              className="luxury-input"
              data-testid="search-input"
            />
          </form>
        )}

        {mobileOpen && (
          <div className="md:hidden border-t border-[#EBE5DF] py-4 flex flex-col gap-3" data-testid="mobile-menu">
            <NavLink to="/" end onClick={() => setMobileOpen(false)}>{t("nav.home")}</NavLink>
            <NavLink to="/shop" onClick={() => setMobileOpen(false)}>{t("nav.shop")}</NavLink>
            <NavLink to="/careers" onClick={() => setMobileOpen(false)}>{t("nav.careers")}</NavLink>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setLang("el")} className={lang === "el" ? "text-[#D4AF37] font-semibold" : ""}>GR</button>
              <button onClick={() => setLang("en")} className={lang === "en" ? "text-[#D4AF37] font-semibold" : ""}>EN</button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
